import { ActiveSubscriptionStatuses, PlatformUserBase, PlatformUserClient, PlatformUserCreative, SubscriptionPlan, UserWorkspaceMembership, WorkspaceInvitationState, WorkspaceSeatType, WorkspaceUserProfile } from 'models/user'
import { UserIsPlatformUserBase, UserIsPlatformUserClient, UserIsPlatformUserCreative } from 'utils/typeguards'
import { shouldUserRequestOrganizationMe, useAuth0 } from 'utils/auth'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useGetOrganizationMe, useGetUserWorkspaces, useUserMe } from 'dataQueries'

import * as Sentry from '@sentry/react'

import constate from 'constate'
import { embedSession } from 'utils/embedSession'
import { logger } from 'utils/logging'
import { pollUserMeUntilWorkspaceMember } from 'utils/helpers/pollUserMeUntilWorkspaceMember'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Which candidate the active workspace came from. The branches are otherwise indistinguishable after the
 * fact — they either write the same sessionStorage key or deliberately write nothing — so a "it opened the
 * wrong workspace" report is undiagnosable without this. A tag rather than a log line: this is steady state
 * on every boot, not an error.
 */
const tagWorkspaceResolvedFrom = (source: 'embed-pin' | 'session' | 'pending-hold' | 'preferred' | 'fallback') =>
  Sentry.setTag('workspaceResolvedFrom', source)

function useUserSetupHook() {
  const { roles, isAuthenticated } = useAuth0()
  const { data: userMeData } = useUserMe(isAuthenticated)
  const queryClient = useQueryClient()

  const embedWorkspaceId = useSyncExternalStore(embedSession.subscribe, embedSession.getWorkspaceId)

  /** Last pin reported as unresolvable, so one failure emits one event instead of one per re-render. */
  const lastUnmatchedPin = useRef<string | null>(null)
  /** Last live pin: the commit where the session dies still holds the pinned selection with a null pin. */
  const lastPinnedWorkspaceId = useRef<string | null>(null)

  const organizationMe = useGetOrganizationMe(shouldUserRequestOrganizationMe(userMeData ?? null))
  const organizationData = useMemo(() => organizationMe.data?.data, [organizationMe])

  // Current user workspace state: can be personal or active subscribed workspaces
  const [currentUserWorkspace, setCurrentUserWorkspace] = useState<WorkspaceUserProfile | null>(null)
  const [isWorkspaceResolved, setIsWorkspaceResolved] = useState(false)


  // Base data of user available for each role
  const baseUserData = useMemo<PlatformUserBase | undefined>(() => {
    if (!userMeData) return undefined

    return {
      id: userMeData.id,
      internalId: (userMeData as PlatformUserBase).internalId,
      platformRole: (userMeData as PlatformUserBase).platformRole,
      publicId: userMeData.publicId,
      name: userMeData.name,
      email: userMeData.email,
      phone: userMeData.phone,
      country: userMeData.country,
      defaultTimezone: userMeData.defaultTimezone,
      language: userMeData.language,
    }
  }, [userMeData])

  /** Client user data, if user is not client this will be undefined */
  const clientData = useMemo(() => {
    if (!userMeData) return undefined
    if (!roles.isClient) return undefined
    if (!UserIsPlatformUserClient(userMeData)) return undefined
    return userMeData as PlatformUserClient
  }, [roles.isClient, userMeData])

  /** Admin user data, if user is not admin, this will be undefined */
  const adminData = useMemo(() => {
    if (!userMeData) return undefined
    if (!roles.isAdmin) return undefined
    if (!UserIsPlatformUserBase(userMeData)) return undefined
    return userMeData as PlatformUserBase
  }, [roles.isAdmin, userMeData])

  /** Creative user data, if user is not creative, this will be undefined */
  const creativeData = useMemo(() => {
    if (!userMeData) return undefined
    if (!roles.isCreative) return undefined
    if (!UserIsPlatformUserCreative(userMeData)) return undefined
    return userMeData as PlatformUserCreative
  }, [roles.isCreative, userMeData])

  /** Array of all workspaces the user is a member of and are non-personal */
  const nonPersonalUserWorkspaces = useMemo(() => (roles.isClient && !!clientData && clientData.workspaces && clientData.workspaces.filter((workspace) => workspace.plan !== SubscriptionPlan.PERSONAL)) || [], [roles, clientData])

  /** Array of all workspaces the user is a member of and are personal */
  const personalUserWorkspaces = useMemo(() => (roles.isClient && !!clientData && clientData.workspaces && clientData.workspaces.filter((workspace) => workspace.plan === SubscriptionPlan.PERSONAL)) || [], [roles, clientData])

  /** Array of personal workspaces with active status */
  const activePersonalUserWorkspaces = useMemo(() => personalUserWorkspaces.filter((workspace) => ActiveSubscriptionStatuses.has(workspace.status) && workspace.isMember) || [], [personalUserWorkspaces])

  /** Array of all workspaces the user is actively subscribed to */
  const activeSubscribedUserWorkspaces = useMemo(() => nonPersonalUserWorkspaces.filter((workspace) => ActiveSubscriptionStatuses.has(workspace.status) && workspace.isMember) || [], [nonPersonalUserWorkspaces])

  /** Whether the user has any active subscription (is a member of any non-personal workspace) */
  const hasUserActiveSubscription = useMemo(() => activeSubscribedUserWorkspaces.length !== 0, [activeSubscribedUserWorkspaces])

  /**
   * Whether the *currently selected* workspace is a paid (non-personal) plan with an active subscription status.
   * Scoped to the selected workspace, because a user can switch into a personal workspace while still being
   * a member of a paid workspace elsewhere.
   * Canonical source for paid/SaaS entitlement — re-exposed by `useWorkspacePlan`.
   *
   * In the embed (ISSUE-2889) the selected workspace is the pinned shop workspace, so its entitlement
   * never comes from the broker's own paid workspace.
   *
   * A RESTRICTED (free) seat never unlocks paid features, even inside a paid workspace (ISSUE-2488):
   * seat type is orthogonal to the workspace plan, so a Restricted Admin/member of a paid workspace
   * still has no paid entitlement.
   */
  const hasWorkspaceActiveSubscription = useMemo(
    () => !!currentUserWorkspace && currentUserWorkspace.isMember && currentUserWorkspace.plan !== SubscriptionPlan.PERSONAL && ActiveSubscriptionStatuses.has(currentUserWorkspace.status) && currentUserWorkspace.seatType !== WorkspaceSeatType.RESTRICTED,
    [currentUserWorkspace]
  )

  /** Whether user is allowed to use invoice by email or not */
  const userCanPayByInvoice = useMemo(() => {
    if (roles.isAdmin) return true
    return hasWorkspaceActiveSubscription || !!organizationData?.invoiceAllowed
  }, [hasWorkspaceActiveSubscription, organizationData?.invoiceAllowed, roles.isAdmin])

  const getWorkspaceItemById = useCallback(
    (id: string) => {
      const searchInActiveWorkspace = activeSubscribedUserWorkspaces.find((workspace) => workspace.id === id)
      const searchInPersonalWorkspace = activePersonalUserWorkspaces.find((workspace) => workspace.id === id)

      return searchInActiveWorkspace || searchInPersonalWorkspace
    },
    [activeSubscribedUserWorkspaces, activePersonalUserWorkspaces]
  )

  /** Store user workspace id in session storage. Client can use different workspace in different tabs. */
  useEffect(() => {
    if (!clientData) {
      setIsWorkspaceResolved(false)
      // A dead embed session also drops the synthesized CLIENT role, so the pinned selection would
      // otherwise survive here with no pin left to guard it against persistence.
      if (!embedWorkspaceId && currentUserWorkspace?.id === lastPinnedWorkspaceId.current) {
        setCurrentUserWorkspace(null)
      }
      return
    }

    if (embedWorkspaceId) {
      const pinnedWorkspace = clientData.workspaces?.find(
        (workspace) => workspace.id === embedWorkspaceId && workspace.isMember
      ) ?? null
      if (!pinnedWorkspace && lastUnmatchedPin.current !== embedWorkspaceId) {
        // Session-init provisions the membership before the bootstrap token exists, so this is an
        // inconsistent state; surface it instead of silently operating in another workspace.
        // Latched on the pin: this effect re-runs on every userMe object change.
        lastUnmatchedPin.current = embedWorkspaceId
        logger.error('Embed session workspace is not among the user memberships', {
          embedWorkspaceId,
          membershipIds: clientData.workspaces?.map((workspace) => workspace.id) ?? [],
          memberFlags: clientData.workspaces?.map((workspace) => workspace.isMember) ?? [],
        }, {
          context: { component: 'UserDataContext', action: 'embedWorkspacePinUnmatched' },
        })
        // The membership may still be propagating; bounded userMe refetches re-run this effect so a
        // transient lag self-heals instead of stranding the embed on an empty flow all session.
        void pollUserMeUntilWorkspaceMember(queryClient, embedWorkspaceId)
      }
      tagWorkspaceResolvedFrom('embed-pin')
      if (currentUserWorkspace !== pinnedWorkspace) {
        setCurrentUserWorkspace(pinnedWorkspace)
      }
      setIsWorkspaceResolved(true)
      return
    }

    const noPersonalWorkspace = activePersonalUserWorkspaces.length === 0

    if (!hasUserActiveSubscription && noPersonalWorkspace) {
      setCurrentUserWorkspace(null)
      setIsWorkspaceResolved(true)
      return
    }

    // Respect the stored workspace (user can switch via dropdown)
    const sessionWorkspaceId = sessionStorage.getItem('workspaceId')
    const matchedWorkspace = getWorkspaceItemById(sessionWorkspaceId || '')

    if (!!matchedWorkspace) {
      tagWorkspaceResolvedFrom('session')
      if (currentUserWorkspace !== matchedWorkspace) {
        setCurrentUserWorkspace(matchedWorkspace)
      }

      setIsWorkspaceResolved(true)
      return
    }

    // A just-accepted invitation still appears as PENDING in userMe until the membership
    // propagates, so getWorkspaceItemById can't resolve it yet. Keep the stored id and current
    // selection intact — nothing refetches userMe on its own (no refetchInterval, no refetch on
    // focus), so the accept flow drives the follow-up refetches via
    // pollUserMeUntilWorkspaceMember until the membership lands and this effect re-runs.
    //
    // This has to be decided BEFORE the persisted workspace below. The stored id here is one the user
    // just chose but that is momentarily unresolvable, whereas the persisted one is an older choice that
    // resolves fine — so consulting the server first would let the old workspace win, overwrite the
    // accepted id in this tab, and strand the user outside the workspace they just joined (QA-258). The
    // poll cannot recover from that, because it only refetches userMe and never revisits storage.
    const isStoredWorkspacePendingInvite = !!sessionWorkspaceId && nonPersonalUserWorkspaces.some((workspace) =>
      workspace.id === sessionWorkspaceId && ActiveSubscriptionStatuses.has(workspace.status) && workspace.hasOwnProperty('invitationId') && workspace.invitationState === WorkspaceInvitationState.PENDING
    )

    if (isStoredWorkspacePendingInvite) {
      tagWorkspaceResolvedFrom('pending-hold')
      setIsWorkspaceResolved(!!currentUserWorkspace)
      return
    }

    // Falls back to the server-persisted choice, which is what carries a selection past this tab —
    // across reloads in a new tab, and across browsers and devices. Resolved through
    // getWorkspaceItemById like every other candidate, so a workspace the user has since lost access to
    // (left, or had the membership revoked) falls through instead of selecting nothing.
    const persistedWorkspace = getWorkspaceItemById(clientData.preferredWorkspaceId || '')

    if (!!persistedWorkspace) {
      tagWorkspaceResolvedFrom('preferred')
      sessionStorage.setItem('workspaceId', persistedWorkspace.id)
      if (currentUserWorkspace !== persistedWorkspace) {
        setCurrentUserWorkspace(persistedWorkspace)
      }

      setIsWorkspaceResolved(true)
      return
    }

    // Fallback: no valid workspace found in storage
    const fallbackWorkspace = !hasUserActiveSubscription ? activePersonalUserWorkspaces[0] : activeSubscribedUserWorkspaces[0]
    tagWorkspaceResolvedFrom('fallback')
    sessionStorage.setItem('workspaceId', fallbackWorkspace.id)
    if (currentUserWorkspace !== fallbackWorkspace) {
      setCurrentUserWorkspace(fallbackWorkspace)
    }

    setIsWorkspaceResolved(true)
    // currentUserWorkspace intentionally excluded — used only for ID comparison, not as reactive dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientData, activeSubscribedUserWorkspaces, getWorkspaceItemById, hasUserActiveSubscription, activePersonalUserWorkspaces, nonPersonalUserWorkspaces, embedWorkspaceId, queryClient])

  // Keep session storage workspace id in sync with current user workspace. Session only: this effect also
  // runs for selections the user never made (the deterministic fallback, landing on a property link), so
  // it must not be the thing that records a lasting preference — an explicit switch does that server-side.
  useEffect(() => {
    if (!currentUserWorkspace) return
    // The embed pin is session-scoped: persisting it would leak the shop workspace into the
    // user's own (non-embed) selection on this origin.
    if (embedWorkspaceId) {
      lastPinnedWorkspaceId.current = embedWorkspaceId
      return
    }
    // The pin dies one commit before the selection does, so guard on the pinned id too.
    if (currentUserWorkspace.id === lastPinnedWorkspaceId.current) return

    const sessionWorkspaceId = sessionStorage.getItem('workspaceId')
    if (sessionWorkspaceId !== currentUserWorkspace.id) {
      sessionStorage.setItem('workspaceId', currentUserWorkspace.id)
    }
  }, [currentUserWorkspace, embedWorkspaceId])

  /** User workspace id for fetching properties detail */
  const userPropertyWorkspaceId = useMemo(() => currentUserWorkspace?.id || '', [currentUserWorkspace?.id])

  /** Full workspace membership data from /user/workspace endpoint */
  const userWorkspacesQuery = useGetUserWorkspaces(isAuthenticated && roles.isClient)

  useEffect(() => {
    if (userWorkspacesQuery.isError) {
      logger.error('Failed to load user workspaces', userWorkspacesQuery.error, {
        context: { component: 'UserDataContext', action: 'useGetUserWorkspaces' }
      })
    }
  }, [userWorkspacesQuery.isError, userWorkspacesQuery.error])

  /**
   * Personal workspaces to show in the workspace switcher (ISSUE-2391).
   *
   * `personalUserWorkspaces` is derived from GET /user/me, which is NOT subject to the
   * `hide-unused-personal-workspaces` filtering (ISSUE-2074). GET /user/workspace IS, so we
   * intersect the two by id: an unused auto-created personal workspace hidden by the backend is
   * absent from `userWorkspacesQuery` and therefore dropped from the switcher, while it reappears
   * automatically once the backend surfaces it again (e.g. it becomes the user's only workspace) —
   * no separate FE state to keep in sync.
   *
   * Falls back to the unfiltered list until the query resolves so the switcher doesn't flash empty,
   * and this keeps `personalUserWorkspaces` untouched for every other consumer. When the
   * backend flag is off, GET /user/workspace returns everything, making this a no-op.
   */
  const visiblePersonalUserWorkspaces = useMemo(() => {
    if (!userWorkspacesQuery.data) return personalUserWorkspaces

    const visibleWorkspaceIds = new Set(userWorkspacesQuery.data.workspaces?.map((ws) => ws.workspaceId) ?? [])
    return personalUserWorkspaces.filter((workspace) => visibleWorkspaceIds.has(workspace.id))
  }, [personalUserWorkspaces, userWorkspacesQuery.data])

  // Refetch workspace memberships when the user switches workspaces so freshly
  // transitioned statuses (e.g. IN_GRACE_PERIOD → ACTIVE after subscription confirmation)
  // are reflected without a manual page reload. The initial '' → id transition is skipped
  // because the query already auto-fetches once `enabled` flips true.
  const previousWorkspaceIdRef = useRef<string>('')
  useEffect(() => {
    if (!userPropertyWorkspaceId || !isAuthenticated || !roles.isClient) return

    if (previousWorkspaceIdRef.current && previousWorkspaceIdRef.current !== userPropertyWorkspaceId) {
      userWorkspacesQuery.refetch()
    }
    previousWorkspaceIdRef.current = userPropertyWorkspaceId
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch is stable; intentionally key on workspace id + auth/role only
  }, [userPropertyWorkspaceId, isAuthenticated, roles.isClient])

  /** Current workspace membership with enriched data (ccEmails, members, etc.) */
  const currentUserWorkspaceMembership = useMemo<UserWorkspaceMembership | undefined>(
    () => userWorkspacesQuery.data?.workspaces?.find((ws) => ws.workspaceId === userPropertyWorkspaceId),
    [userWorkspacesQuery.data, userPropertyWorkspaceId]
  )

  /** CC emails for the current workspace */
  const currentWorkspaceCcEmails = useMemo(() => currentUserWorkspaceMembership?.ccEmails ?? [], [currentUserWorkspaceMembership])

  /** @deprecated ISSUE-484 - Remove this after multi-workspace support is released. */
  const pendingInvitationWorkspace = useMemo(() => {
    return nonPersonalUserWorkspaces.find((workspace) => ActiveSubscriptionStatuses.has(workspace.status) && workspace.hasOwnProperty('invitationId') && workspace.invitationState === WorkspaceInvitationState.PENDING)
  }, [nonPersonalUserWorkspaces])

  const pendingInvitationWorkspaceList = useMemo(() => {
    return nonPersonalUserWorkspaces.filter((workspace) => ActiveSubscriptionStatuses.has(workspace.status) && workspace.hasOwnProperty('invitationId') && workspace.invitationState === WorkspaceInvitationState.PENDING)
  }, [nonPersonalUserWorkspaces])

  useEffect(() => {
    if (!shouldUserRequestOrganizationMe(userMeData ?? null)) return

    organizationMe.refetch()

    // Only refetch when userMeData changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMeData])

  return {
    clientData,
    adminData,
    creativeData,
    baseUserData,
    organizationMe,
    organizationData,
    nonPersonalUserWorkspaces,
    activePersonalUserWorkspaces,
    activeSubscribedUserWorkspaces,
    hasUserActiveSubscription,
    hasWorkspaceActiveSubscription,
    userCanPayByInvoice,
    currentUserWorkspace,
    setCurrentUserWorkspace,
    personalUserWorkspaces,
    visiblePersonalUserWorkspaces,
    userPropertyWorkspaceId,
    userWorkspacesQuery,
    currentUserWorkspaceMembership,
    currentWorkspaceCcEmails,
    pendingInvitationWorkspace,
    pendingInvitationWorkspaceList,
    getWorkspaceItemById,
    isWorkspaceResolved,
  }
}

export const [UserDataContextProvider, useUserData] = constate(useUserSetupHook)
